import { useMemo, useState } from "react";

const DEFAULT_MANAGEMENT_LEVELS = [
  "No Management Experience",
  "Team Lead / Supervisor (1–3 people)",
  "Manager (manage a team)",
  "Director / Executive (manage managers)",
];

const normalize = (v) => {
  if (v == null) return "";
  if (Array.isArray(v)) v = v[0] ?? "";
  if (typeof v === "object") v = v.value ?? v.label ?? "";
  return String(v).replace(/\s+/g, " ").trim();
};

export default function ManagementExperienceSelector({ form, updateField }) {
  const selected = useMemo(() => normalize(form?.Management_Exp), [form?.Management_Exp]);

  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");

  const setMsg = (msg) => {
    setMessage(msg);
    if (msg) setTimeout(() => setMessage(""), 1500);
  };

  const selectManagement = (value) => {
    updateField("Management_Exp", String(value)); // store string
    setMsg("Saved successfully ✅");
  };

  const addCustom = () => {
    const value = normalize(custom);
    if (!value) return setMsg("Please enter management experience");

    updateField("Management_Exp", value);
    setCustom("");
    setAdding(false);
    setMsg("Saved successfully ✅");
  };

  const clearSelection = () => {
    updateField("Management_Exp", "");
    setMsg("");
  };

  return (
    <div className="rounded-xl border border-pink-400/20 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-pink-300 text-sm font-semibold">Your Management Experience</p>
        <span className="text-xs text-white/60 whitespace-nowrap">{selected ? "1 selected" : "Pick 1"}</span>
      </div>

      {/* ✅ Mobile 1 col, Desktop 2 cols */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        {DEFAULT_MANAGEMENT_LEVELS.map((m) => (
          <label
            key={m}
            className="min-w-0 flex items-start gap-2 text-[13px] sm:text-sm text-white/85 leading-snug cursor-pointer hover:text-white"
          >
            <input
              type="radio"
              name="management"
              className="mt-1 accent-pink-500 flex-shrink-0"
              checked={selected === m}
              onChange={() => selectManagement(m)}
            />
            <span className="min-w-0 whitespace-normal break-words">{m}</span>
          </label>
        ))}
      </div>

      {/* Selected row */}
      {!!selected && (
        <div className="mt-3 flex items-center justify-between bg-[#11111a] px-3 py-2 rounded border border-pink-400/30">
          <p className="text-xs text-pink-300 min-w-0">
            ✅ Selected: <span className="text-white break-words">{selected}</span>
          </p>

          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-red-400 underline hover:text-red-300 ml-3 flex-shrink-0"
          >
            Clear
          </button>
        </div>
      )}

      {/* Add custom */}
      {!adding && (
        <button
          type="button"
          className="mt-3 text-pink-400 underline text-sm"
          onClick={() => {
            setAdding(true);
            setMessage("");
          }}
        >
          + Add Management Experience
        </button>
      )}

      {adding && (
        <div className="mt-3">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Enter management experience"
            className="w-full p-2 bg-[#11111a] border border-pink-400/40 rounded text-sm"
          />

          <div className="flex gap-2 mt-2">
            <button type="button" onClick={addCustom} className="bg-pink-500 px-3 py-1 rounded text-black text-sm">
              Add
            </button>

            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setCustom("");
                setMessage("");
              }}
              className="bg-gray-600 px-3 py-1 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {message && (
        <p className={`text-sm mt-2 ${message.includes("Saved") ? "text-green-400" : "text-red-400"}`}>
          {message}
        </p>
      )}
    </div>
  );
}